import { Link } from "react-router";
import { Logo } from "@/components/layout/logo";
import { useAppContext } from "@/providers";
import { useState, useRef, useEffect } from "react";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";
import {
  Target,
  Users,
  TrendingUp,
  ShieldCheck,
  Sparkles,
  Rocket,
  Mail,
  MapPin,
  type LucideIcon,
} from "lucide-react";

// ─── NCircle brand symbol ───────────────────────────────────────────────────
function BrandSymbol({ size = 40 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 258 255"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M114.01 193.952C114.01 157.612 94.47 124.092 62.86 106.172L10.06 76.2416C3.59 91.6116 0 108.502 0 126.232C0 140.592 2.35 154.412 6.69 167.312C8.41 172.422 14.03 175.112 19.13 173.382C38.73 166.732 58.31 160.412 59.88 159.592C65.97 157.482 70.58 165.462 65.79 169.682L33.72 198.372C29.7 201.962 29.27 208.172 32.87 212.192C53.35 235.062 81.88 250.572 114.01 254.272V193.952Z"
        fill="#0467C4"
      />
      <path
        d="M79.37 80.7916C110.23 98.2816 148.01 98.2816 178.87 80.7916L232.94 50.1416C214.59 25.0916 187.42 6.93158 155.99 0.211585C150.66 -0.928415 145.44 2.66158 144.43 8.01158C140.26 30.0216 135.31 53.7316 135.24 55.6616C134.06 62.0016 124.85 62.0616 123.55 55.8116L113.19 7.81158C112.06 2.56158 106.9 -0.888415 101.65 0.241585C70.37 6.97159 43.31 25.0516 25 49.9616L79.37 80.7916Z"
        fill="#FFA500"
      />
      <path
        d="M192.87 169.692C188.03 165.492 192.82 157.432 198.78 159.602L238.69 173.272C243.81 175.022 249.45 172.342 251.17 167.212C255.48 154.342 257.82 140.572 257.82 126.252C257.82 108.602 254.26 91.7816 247.84 76.4516L195.38 106.172C163.77 124.092 144.23 157.622 144.23 193.952V254.202C176.34 250.392 204.83 234.802 225.24 211.852C228.8 207.842 228.38 201.672 224.4 198.082C209.17 184.372 194.34 170.642 192.87 169.692Z"
        fill="#ACE405"
      />
    </svg>
  );
}

function Arrow({ rtl }: { rtl: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      className={rtl ? "rotate-180" : ""}
      aria-hidden
    >
      <path
        d="M5 12h14M13 5l7 7-7 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Editorial content (Arabic / English pairs) ────────────────────────────

type Capability = {
  num: string;
  title: string;
  desc: string;
  Icon: LucideIcon;
  tone: string;
};

const capabilities: Record<"ar" | "en", Capability[]> = {
  ar: [
    {
      num: "٠١",
      title: "صمّم مبادرتك",
      desc:
        "حدّد الهدف، اختر المؤشّرات، وخصّص ميزانية المكافآت — كلّه من لوحة واحدة.",
      Icon: Target,
      tone: "text-primary bg-primary/10",
    },
    {
      num: "٠٢",
      title: "أشرك مجتمعك",
      desc:
        "ادعُ التجار والأهالي للمشاركة، وتابع انضمامهم لحظةً بلحظة.",
      Icon: Users,
      tone: "text-success bg-success/10",
    },
    {
      num: "٠٣",
      title: "قِس الأثر",
      desc:
        "تقارير حيّة تُظهر التقدم نحو الهدف وتوزيع المكافآت بشفافية.",
      Icon: TrendingUp,
      tone: "text-secondary bg-secondary/10",
    },
  ],
  en: [
    {
      num: "01",
      title: "Design your initiative",
      desc:
        "Define the goal, pick the KPIs, and allocate reward budgets — all from one dashboard.",
      Icon: Target,
      tone: "text-primary bg-primary/10",
    },
    {
      num: "02",
      title: "Engage your community",
      desc:
        "Invite merchants and residents to take part, and watch them join in real time.",
      Icon: Users,
      tone: "text-success bg-success/10",
    },
    {
      num: "03",
      title: "Measure the impact",
      desc:
        "Live reports show progress toward your goal and a transparent reward distribution.",
      Icon: TrendingUp,
      tone: "text-secondary bg-secondary/10",
    },
  ],
};

type StepRow = {
  step: string;
  title: string;
  desc: string;
  Icon: LucideIcon;
};

const enablerSteps: Record<"ar" | "en", StepRow[]> = {
  ar: [
    {
      step: "STEP 01",
      title: "افتح حسابك كجهة مبادرة",
      desc: "تحقّق آمن، إعداد سريع، ومحفظة افتراضية تُربط بك مباشرة.",
      Icon: ShieldCheck,
    },
    {
      step: "STEP 02",
      title: "ابنِ مبادرتك",
      desc:
        "اختَر النوع — مشي، زراعة أشجار، توفير، تسوّق محلي — ثم اضبط الأهداف.",
      Icon: Sparkles,
    },
    {
      step: "STEP 03",
      title: "أطلِق وراقِب",
      desc:
        "لوحة معلومات تجمع الأرقام، التحقّقات، والمكافآت في مكانٍ واحد.",
      Icon: Rocket,
    },
  ],
  en: [
    {
      step: "STEP 01",
      title: "Open your enabler account",
      desc:
        "Secure verification, quick setup, and a virtual wallet bound to you.",
      Icon: ShieldCheck,
    },
    {
      step: "STEP 02",
      title: "Build your initiative",
      desc:
        "Pick the type — walk, plant, save, shop local — and dial in your goals.",
      Icon: Sparkles,
    },
    {
      step: "STEP 03",
      title: "Launch and watch",
      desc:
        "One dashboard for numbers, verifications, and reward flow.",
      Icon: Rocket,
    },
  ],
};

type StatCell = { value: string; label: string };

const stats: Record<"ar" | "en", StatCell[]> = {
  ar: [
    { value: "٢٠٠+", label: "مبادرة نشطة" },
    { value: "١,٢٠٠+", label: "تاجر مشارك" },
    { value: "٨٥+", label: "مجتمع محلي" },
    { value: "٤.٢ م", label: "أثر مجتمعي مقاس" },
  ],
  en: [
    { value: "200+", label: "Active initiatives" },
    { value: "1,200+", label: "Merchants on board" },
    { value: "85+", label: "Communities reached" },
    { value: "4.2M", label: "Measured community impact" },
  ],
};

// ─── Component ──────────────────────────────────────────────────────────────
export function LandingPage() {
  const { locale, setLocale } = useAppContext();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const isRTL = locale === "ar";

  const handleLanguageChange = (newLocale: string) => {
    setLocale(newLocale as "en" | "ar");
    setIsLangMenuOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        langMenuRef.current &&
        !langMenuRef.current.contains(event.target as Node)
      ) {
        setIsLangMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const caps = capabilities[locale as "ar" | "en"] ?? capabilities.ar;
  const steps = enablerSteps[locale as "ar" | "en"] ?? enablerSteps.ar;
  const numbers = stats[locale as "ar" | "en"] ?? stats.ar;

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* ─── Top rail ─── */}
      {/* <div className="w-full border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-11 flex items-center justify-between text-[11px] font-sans text-foreground-secondary">
          <span className="tracking-[0.2em]">NCIRCLE · أثر</span>
          <span className="hidden md:inline tracking-[0.2em]">
            EST. 2026 / المدينة المنورة
          </span>
          <span className="tracking-[0.2em] flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-success" />
            LIVE SOON
          </span>
        </div>
      </div> */}

      {/* ─── Header ─── */}
      <header className="w-full border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-3 relative" ref={langMenuRef}>
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="text-sm font-medium text-foreground-secondary hover:text-foreground transition-colors px-3 py-1.5 rounded-lg hover:bg-muted"
            >
              {locale === "ar" ? "EN" : "عربي"}
            </button>
            {isLangMenuOpen && (
              <div className="absolute top-full mt-2 end-0 w-32 bg-card rounded-lg shadow-lg border border-border py-1 z-50 animate-slide-in-top">
                <button
                  onClick={() => handleLanguageChange("ar")}
                  className={cn(
                    "w-full text-start px-4 py-2 text-sm hover:bg-muted transition-colors",
                    locale === "ar"
                      ? "text-primary font-bold"
                      : "text-foreground-secondary",
                  )}
                >
                  العربية
                </button>
                <button
                  onClick={() => handleLanguageChange("en")}
                  className={cn(
                    "w-full text-start px-4 py-2 text-sm hover:bg-muted transition-colors",
                    locale === "en"
                      ? "text-primary font-bold"
                      : "text-foreground-secondary",
                  )}
                >
                  English
                </button>
              </div>
            )}
            <Link
              to="/login"
              className="px-5 py-2 bg-primary text-white rounded-pill text-sm font-semibold hover:bg-primary-dark transition-all active:scale-95 shadow-sm"
            >
              {t(locale, "login")}
            </Link>
          </div>
        </div>
      </header>

      {/* ─── Hero (single CTA — enabler only) ─── */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="col-span-12 lg:col-span-8 space-y-7">
              <span className="inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.2em] text-foreground-secondary border border-border rounded-full px-3 py-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
                {isRTL ? "للجهات المبادرة" : "FOR ENABLERS"}
              </span>

              {isRTL ? (
                <h1 className="font-display font-bold leading-[1.05] text-[44px] sm:text-[60px] md:text-[80px] lg:text-[104px] text-foreground tracking-tight">
                  أثرٌ يُقاس
                  <br />
                  في كلِّ
                  <span className="relative mx-3 inline-block">
                    <span className="relative z-10">مبادرة</span>
                    <span
                      className="absolute inset-x-0 bottom-2 h-3 md:h-5 bg-success/70 -z-0"
                      aria-hidden
                    />
                  </span>
                </h1>
              ) : (
                <h1 className="font-english font-bold leading-[1.05] text-[44px] sm:text-[60px] md:text-[72px] lg:text-[88px] text-foreground tracking-tight">
                  Measurable impact
                  <br />
                  in every
                  <span className="relative mx-3 inline-block">
                    <span className="relative z-10">initiative</span>
                    <span
                      className="absolute inset-x-0 bottom-2 h-3 md:h-5 bg-success/70 -z-0"
                      aria-hidden
                    />
                  </span>
                </h1>
              )}

              <p className="max-w-[640px] text-base md:text-lg text-foreground-secondary leading-relaxed">
                {isRTL
                  ? "أثر منصّة الجهات المبادرة. صمّم مبادرتك، اضبط أهدافك، خصّص ميزانية المكافآت — وراقب الأثر الفعلي في وقتٍ حقيقي."
                  : "Athr is the platform for initiative enablers. Design your initiative, set your goals, allocate reward budgets — and watch the real impact, in real time."}
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-1">
                <Link
                  to="/auth/initiative/request"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-7 py-3.5 rounded-pill font-bold text-sm shadow-sm transition-all active:scale-95"
                >
                  <span>
                    {isRTL ? "أنشئ مبادرتك" : "Launch an initiative"}
                  </span>
                  <Arrow rtl={isRTL} />
                </Link>
                <a
                  href="#capabilities"
                  className="inline-flex items-center gap-2 ms-1 font-sans text-[11px] tracking-[0.2em] text-foreground-secondary hover:text-foreground transition-colors"
                >
                  <span className="w-8 h-px bg-border" />
                  <span>
                    {isRTL ? "اعرف المزيد" : "EXPLORE THE PLATFORM"}
                  </span>
                </a>
              </div>
            </div>

            {/* Right: simple symbol with 3 pillar callout chips */}
            <div className="col-span-12 lg:col-span-4 relative flex justify-center lg:justify-end">
              <div className="relative w-[300px] h-[300px] md:w-[360px] md:h-[360px] flex items-center justify-center">
                <div
                  className="absolute inset-0 rounded-full bg-muted"
                  aria-hidden
                />
                <div
                  className="absolute inset-6 rounded-full border border-border"
                  aria-hidden
                />
                <div
                  className="absolute inset-14 rounded-full border border-border/60"
                  aria-hidden
                />

                <div className="relative z-10 p-5 bg-card rounded-2xl shadow-md border border-border">
                  <BrandSymbol size={64} />
                </div>

                <PillarChip
                  className="absolute -top-1 start-2 md:top-2 md:start-6"
                  color="bg-success"
                  label={isRTL ? "الخضراء" : "Green"}
                />
                <PillarChip
                  className="absolute top-1/2 -end-3 md:-end-2 -translate-y-1/2"
                  color="bg-primary"
                  label={isRTL ? "المجتمعي" : "Community"}
                />
                <PillarChip
                  className="absolute -bottom-1 start-6 md:bottom-4 md:start-12"
                  color="bg-secondary"
                  label={isRTL ? "الاجتماعي" : "Social"}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Capabilities (3 columns, editorial) ─── */}
      <section id="capabilities" className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="flex items-end justify-between gap-6 mb-12 md:mb-16">
            <div className="space-y-3 max-w-2xl">
              <span className="text-[11px] font-sans tracking-[0.2em] text-foreground-secondary">
                {isRTL ? "ما الذي يقدّمه أثر" : "WHAT YOU GET"}
              </span>
              <h2 className="font-display font-bold leading-[1.1] text-3xl md:text-5xl text-foreground tracking-tight">
                {isRTL
                  ? "أدواتٌ تحوّل النوايا إلى نتائج."
                  : "Tools that turn intent into outcomes."}
              </h2>
            </div>
            <span className="hidden md:inline-block text-[11px] font-sans tracking-[0.2em] text-foreground-secondary whitespace-nowrap pb-2">
              03 · CAPABILITIES
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-12">
            {caps.map((c, i) => (
              <div
                key={c.num}
                className={cn(
                  "space-y-5 md:pe-8",
                  i < caps.length - 1 && "md:border-e md:border-border",
                )}
              >
                <div className="flex items-center justify-between">
                  <div
                    className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center",
                      c.tone,
                    )}
                  >
                    <c.Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <span className="font-sans text-sm tracking-[0.15em] text-foreground-secondary">
                    {c.num}
                  </span>
                </div>
                <h3 className="font-display font-bold text-2xl text-foreground tracking-tight leading-tight">
                  {c.title}
                </h3>
                <p className="text-foreground-secondary leading-relaxed text-[15px]">
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works (steps stacked, mono labels) ─── */}
      <section className="border-t border-border bg-muted/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 lg:col-span-4 space-y-3">
              <span className="text-[11px] font-sans tracking-[0.2em] text-foreground-secondary">
                {isRTL ? "كيف تعمل" : "HOW IT WORKS"}
              </span>
              <h2 className="font-display font-bold leading-[1.1] text-3xl md:text-5xl text-foreground tracking-tight">
                {isRTL ? "ثلاث خطوات وأنت في الميدان." : "Three steps to launch."}
              </h2>
              <p className="text-foreground-secondary leading-relaxed text-[15px] max-w-md pt-2">
                {isRTL
                  ? "من فتح الحساب إلى إطلاق المبادرة، أثرٌ يُسهّل الطريق ويُبقيك في القيادة."
                  : "From opening your account to launching, Athr clears the way and keeps you in control."}
              </p>
            </div>

            <div className="col-span-12 lg:col-span-8 lg:ps-12 lg:border-s lg:border-border">
              <ol className="space-y-10 md:space-y-12">
                {steps.map((s) => (
                  <li
                    key={s.step}
                    className="grid grid-cols-12 gap-4 md:gap-6 items-start"
                  >
                    <div className="col-span-12 md:col-span-3 flex items-center gap-3 md:flex-col md:items-start md:gap-3 md:pt-1">
                      <div className="w-11 h-11 rounded-xl bg-card border border-border text-primary flex items-center justify-center shrink-0">
                        <s.Icon className="w-5 h-5" strokeWidth={1.8} />
                      </div>
                      <span className="font-sans text-[11px] tracking-[0.2em] text-foreground-secondary">
                        {s.step}
                      </span>
                    </div>
                    <div className="col-span-12 md:col-span-9 space-y-2">
                      <h3 className="font-display font-bold text-xl md:text-2xl text-foreground tracking-tight leading-snug">
                        {s.title}
                      </h3>
                      <p className="text-foreground-secondary leading-relaxed text-[15px]">
                        {s.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Numbers (4 stat cells, left-aligned) ─── */}
      <section className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
          <div className="flex items-end justify-between gap-6 mb-8 md:mb-12">
            <span className="text-[11px] font-sans tracking-[0.2em] text-foreground-secondary">
              {isRTL ? "أرقامٌ مختارة" : "BY THE NUMBERS"}
            </span>
            <span className="text-[11px] font-sans tracking-[0.2em] text-foreground-secondary">
              YTD · 2026
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-border md:divide-y-0 border border-border rounded-2xl overflow-hidden">
            {numbers.map((n) => (
              <div key={n.label} className="p-6 md:p-8 space-y-1">
                <p className="font-display font-bold text-3xl md:text-5xl text-foreground tracking-tight tabular-nums">
                  {n.value}
                </p>
                <p className="text-[13px] text-foreground-secondary font-medium leading-snug">
                  {n.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Closing CTA ─── */}
      <section className="border-t border-border bg-primary text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="col-span-12 md:col-span-8 space-y-5">
              <span className="inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.2em] text-white/70">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-success" />
                {isRTL ? "ابدأ اليوم" : "GET STARTED"}
              </span>
              <h2 className="font-display font-bold leading-[1.05] text-3xl sm:text-4xl md:text-5xl tracking-tight">
                {isRTL
                  ? "اطلِق مبادرتك. الأثر يبدأ بقرار."
                  : "Launch your initiative. Impact begins with a decision."}
              </h2>
              <p className="text-white/80 max-w-2xl leading-relaxed">
                {isRTL
                  ? "حسابك جاهز خلال دقائق. لا أكواد، لا تكاملات معقّدة — لوحة واحدة تكفي."
                  : "Your account is ready in minutes. No code, no heavy integrations — one dashboard is enough."}
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 flex md:justify-end">
              <Link
                to="/auth/initiative/request"
                className="inline-flex items-center gap-2 bg-white text-primary px-7 py-3.5 rounded-pill font-bold text-sm hover:bg-neutral-100 transition-all active:scale-95 shadow-md"
              >
                <span>{isRTL ? "أنشئ مبادرتك" : "Launch an initiative"}</span>
                <Arrow rtl={isRTL} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer (matches marketing site) ─── */}
      <SiteFooter isRTL={isRTL} />
    </div>
  );
}

// ─── Pillar callout chip ───────────────────────────────────────────────────
function PillarChip({
  className,
  color,
  label,
}: {
  className?: string;
  color: string;
  label: string;
}) {
  return (
    <div
      className={cn(
        "z-20 inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.15em]",
        "bg-card px-3 py-1.5 border border-border rounded-full shadow-sm",
        className,
      )}
    >
      <span className={cn("inline-block w-2 h-2 rounded-full", color)} />
      <span className="text-foreground">{label}</span>
    </div>
  );
}

// ─── Social icons (inline SVG, matching the marketing site set) ────────────
function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M9 8H6v4h3v12h5V12h3.642L18 8h-4V6.333C14 5.378 14.192 5 15.115 5H18V0h-3.808C10.596 0 9 1.583 9 4.615V8z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.95a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
    </svg>
  );
}

const FOOTER_SOCIALS = [
  {
    href: "https://www.linkedin.com/company/ncircleai/",
    label: "LinkedIn",
    Icon: LinkedInIcon,
  },
  {
    href: "https://www.facebook.com/ncircle.ai",
    label: "Facebook",
    Icon: FacebookIcon,
  },
  {
    href: "https://www.instagram.com/ncircle.ai",
    label: "Instagram",
    Icon: InstagramIcon,
  },
  {
    href: "https://www.tiktok.com/@ncircle.official",
    label: "TikTok",
    Icon: TikTokIcon,
  },
];

// ─── Site footer (mirrors marketing site editorial style) ──────────────────
function SiteFooter({ isRTL }: { isRTL: boolean }) {
  const cols = isRTL
    ? [
        {
          title: "المنصة",
          links: [
            { href: "#capabilities", label: "ما الذي يقدّمه" },
            { href: "#capabilities", label: "كيف يعمل" },
            { href: "/auth/initiative/request", label: "أنشئ مبادرة" },
            { href: "/login", label: "تسجيل الدخول" },
          ],
        },
        {
          title: "المبادرات",
          links: [
            { href: "#", label: "جُذور · زراعة" },
            { href: "#", label: "امشِ · مشي" },
            { href: "#", label: "متاجر المجتمع" },
            { href: "#", label: "Smart Saver" },
          ],
        },
        {
          title: "قانوني",
          links: [
            { href: "/privacy-policy", label: "سياسة الخصوصية" },
            { href: "/terms-and-conditions", label: "الشروط والأحكام" },
            { href: "/contact-us", label: "تواصل معنا" },
          ],
        },
      ]
    : [
        {
          title: "PLATFORM",
          links: [
            { href: "#capabilities", label: "What you get" },
            { href: "#capabilities", label: "How it works" },
            { href: "/auth/initiative/request", label: "Launch initiative" },
            { href: "/login", label: "Sign in" },
          ],
        },
        {
          title: "INITIATIVES",
          links: [
            { href: "#", label: "Juthoor · Planting" },
            { href: "#", label: "Walk · Movement" },
            { href: "#", label: "Community Shops" },
            { href: "#", label: "Smart Saver" },
          ],
        },
        {
          title: "LEGAL",
          links: [
            { href: "/privacy-policy", label: "Privacy policy" },
            { href: "/terms-and-conditions", label: "Terms & conditions" },
            { href: "/contact-us", label: "Contact us" },
          ],
        },
      ];

  return (
    <footer className="relative w-full bg-neutral-900 text-neutral-100 overflow-hidden">
      {/* Top label strip */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-4 flex items-center gap-6 md:gap-10 overflow-hidden whitespace-nowrap text-[11px] font-sans tracking-[0.25em] text-neutral-400">
          <span>دائرة الأثر / NCIRCLE</span>
          <span className="text-neutral-700">·</span>
          <span>EST. 2026 / المدينة المنورة</span>
          <span className="text-neutral-700">·</span>
          <span className="hidden md:inline">
            GREEN · SOCIAL · COMMUNITY
          </span>
          <span className="hidden md:inline text-neutral-700">·</span>
          <span className="hidden md:inline">NC · REWARD</span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand block */}
          <div className="md:col-span-4 space-y-6">
            <div className="flex items-center gap-3">
              <BrandSymbol size={36} />
              <span className="font-display font-bold text-xl tracking-tight">
                NCircle · أثر
              </span>
            </div>
            <p className="text-neutral-400 text-[15px] leading-relaxed max-w-sm">
              {isRTL
                ? "منصّة الأثر السعودية — تكافئك على كلِّ خطوةٍ نحو بيئةٍ أنقى، ومجتمعٍ أقوى، ودائرةٍ لا تنقطع."
                : "Saudi Arabia's impact platform — rewarding every step toward a cleaner environment, a stronger community, and an unbroken circle."}
            </p>
            <div className="flex items-center gap-3">
              {FOOTER_SOCIALS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-neutral-300 hover:bg-white hover:text-neutral-900 hover:border-white transition-all"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {cols.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <p className="font-sans text-[11px] tracking-[0.2em] text-neutral-500 mb-4">
                {col.title}
              </p>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.href.startsWith("/") ? (
                      <Link
                        to={l.href}
                        className="text-neutral-300 hover:text-success transition-colors text-[14px]"
                      >
                        {l.label}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        className="text-neutral-300 hover:text-success transition-colors text-[14px]"
                      >
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact */}
          <div className="md:col-span-2 space-y-4">
            <p className="font-sans text-[11px] tracking-[0.2em] text-neutral-500">
              {isRTL ? "تواصل" : "CONTACT"}
            </p>
            <a
              href="mailto:support@ncircle.ai"
              className="inline-flex items-center gap-2 text-success hover:text-success-light transition-colors text-[14px]"
            >
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              support@ncircle.ai
            </a>
            <p className="text-neutral-400 text-[13px] leading-relaxed flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
              <span>
                {isRTL ? "الرياض · المدينة المنورة" : "Riyadh · Madinah"}
                <br />
                {isRTL
                  ? "المملكة العربية السعودية"
                  : "Kingdom of Saudi Arabia"}
              </span>
            </p>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-sans text-[11px] tracking-[0.2em] text-neutral-500">
          <span>
            © {new Date().getFullYear()} NCIRCLE ·{" "}
            {isRTL ? "جميع الحقوق محفوظة" : "ALL RIGHTS RESERVED"}
          </span>
          <span className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-success" />
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary" />
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="ms-1">GREEN · SOCIAL · COMMUNITY</span>
          </span>
        </div>
      </div>
    </footer>
  );
}
